import React, { useState, useEffect, useRef } from 'react';
import API_BASE from '../config';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { useUser } from '@clerk/clerk-react';
import toast from "react-hot-toast";
import { creditAwareFetch, emitCreditsChanged } from '../hooks/creditAwareFetch';

const SIGNAL_LABELS = { funding: 'Funding Round', hiring: 'Hiring Activity', leadership_change: 'Leadership Change', news: 'Company News', tech_change: 'Technology Change' };
const SIGNAL_COLOURS = {
  funding: { bg: '#F0FFF4', text: '#276749', border: '#276749' },
  hiring: { bg: '#EBF8FF', text: '#2B6CB0', border: '#2B6CB0' },
  leadership_change: { bg: '#FFF5F5', text: '#8B1A1A', border: '#8B1A1A' },
  news: { bg: '#FFFFF0', text: '#B7791F', border: '#B7791F' },
  tech_change: { bg: '#FAF5FF', text: '#553C9A', border: '#553C9A' },
};
const ACTION_LABELS = { icp_generated: 'ICP Generated', icp_saved: 'ICP Saved', discovery_saved: 'Discovery Brief Saved', sequence_generated: 'Sequence Generated', sequence_approved: 'Sequence Approved', sequence_pushed: 'Pushed to ManyReach', sequence_edited: 'Sequence Edited', prospect_imported: 'Prospects Imported', prospect_added: 'Prospect Added', contact_added: 'Contact Added', client_created: 'Client Created', intelligence_sweep: 'Intelligence Sweep', intelligence_sent: 'Intelligence Digest Sent' };
const ACTION_COLOURS = { icp_generated: '#8B1A1A', icp_saved: '#8B1A1A', discovery_saved: '#276749', sequence_generated: '#4A5568', sequence_approved: '#276749', sequence_pushed: '#276749', sequence_edited: '#4A5568', prospect_imported: '#B7791F', prospect_added: '#B7791F', contact_added: '#B7791F', client_created: '#276749', intelligence_sweep: '#8B1A1A', intelligence_sent: '#8B1A1A' };

function fmt(ts) {
  const d = new Date(ts), now = new Date(), diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function Tag({ colour, bg, children }) {
  return <span style={{ fontSize: '0.68rem', fontWeight: '600', color: colour, backgroundColor: bg, padding: '0.15rem 0.5rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{children}</span>;
}

function SectionLabel({ children, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
      <span style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>{children}</span>
      {count !== undefined && <span style={{ fontSize: '0.68rem', color: '#8B1A1A', backgroundColor: '#F5E6E6', padding: '0.1rem 0.45rem', borderRadius: '20px', fontWeight: '600' }}>{count}</span>}
    </div>
  );
}

function ContactPicker({ companyName, contacts, onSelect, onClose, generatingFor }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', width: '480px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.16)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1A1A1A' }}>Generate sequence</h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#888' }}>Select a contact at {companyName}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {contacts.map(ct => (
            <div key={ct.id} style={{ border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', backgroundColor: '#FAFAF8' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{ct.job_title}</p>
              </div>
              <button onClick={() => onSelect(ct.id, ct.client_id)} disabled={generatingFor === ct.id}
                style={{ fontSize: '0.78rem', color: '#fff', background: generatingFor === ct.id ? '#AAA' : '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer', flexShrink: 0 }}>
                {generatingFor === ct.id ? 'Generating…' : 'Sequence'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignalPanel({ signal, onClose, onSequence }) {
  const [allSignals, setAllSignals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: '', to_name: '', subject: '' });
  const [generatingFor, setGeneratingFor] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [s, c] = await Promise.all([
          fetch(`${API_BASE}/companies/${signal.company_id}/signals`).then(r => r.json()),
          fetch(`${API_BASE}/companies/${signal.company_id}/contacts`).then(r => r.json())
        ]);
        setAllSignals(Array.isArray(s) ? s : (s.signals || []));
        setContacts(Array.isArray(c) ? c : []);
      } catch (e) {}
      setLoading(false);
    }
    load();
  }, [signal.company_id]);

  async function handleSend() {
    if (!sendForm.to_email) return;
    setSending(true);
    const toastId = toast.loading('Sending signals to ' + sendForm.to_email + '...');
    try {
      await fetch(`${API_BASE}/clients/${signal.client_id}/send-signals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: signal.company_id, company_name: signal.company_name, signals: allSignals, ...sendForm })
      });
      setSendSuccess(true); setShowSendForm(false);
      toast.success('Signals sent to ' + sendForm.to_email, { id: toastId });
    } catch (e) {
      toast.error('Could not send: ' + e.message, { id: toastId });
    }
    setSending(false);
  }

  async function handleSequence(contactId, clientId) {
    setGeneratingFor(contactId);
    await onSequence(contactId, clientId);
    setGeneratingFor(null);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: '520px', height: '100vh', backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.5rem', borderBottom: '0.5px solid #E8E4E0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{signal.client_name}</p>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: '#8B1A1A' }}>{signal.company_name}</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#888' }}>{allSignals.length} signals detected</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer' }}>×</button>
        </div>
        {loading ? (
          <p style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</p>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <SectionLabel count={allSignals.length}>Signals detected</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {allSignals.map(s => {
                  const c = SIGNAL_COLOURS[s.signal_type] || { bg: '#F7F7F7', text: '#888', border: '#888' };
                  return (
                    <div key={s.id} style={{ backgroundColor: c.bg, borderRadius: '8px', padding: '1rem', borderLeft: `3px solid ${c.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                        <Tag colour={c.text} bg="rgba(255,255,255,0.6)">{SIGNAL_LABELS[s.signal_type] || s.signal_type}</Tag>
                        <span style={{ fontSize: '0.72rem', color: c.text, fontWeight: '600' }}>Score {s.relevance_score}/10</span>
                      </div>
                      <p style={{ margin: '0 0 0.4rem', fontSize: '0.88rem', color: '#1A1A1A', fontWeight: '500', lineHeight: '1.4' }}>{s.signal_data?.summary}</p>
                      <p style={{ margin: 0, fontSize: '0.72rem', color: '#666' }}>
                        {s.source_url ? <a href={s.source_url} target="_blank" rel="noreferrer" style={{ color: '#2B6CB0' }}>{s.source}</a> : s.source}
                        {' · '}{new Date(s.detected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
            {contacts.length > 0 && (
              <div>
                <SectionLabel count={contacts.length}>Contacts at {signal.company_name}</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {contacts.map(ct => {
                    const e = ct.enrichment_data || {};
                    return (
                      <div key={ct.id} style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                            <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{ct.job_title}</p>
                          </div>
                          <button onClick={() => handleSequence(ct.id, ct.client_id)} disabled={generatingFor === ct.id}
                            style={{ fontSize: '0.75rem', color: '#fff', background: generatingFor === ct.id ? '#AAA' : '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer', flexShrink: 0 }}>
                            {generatingFor === ct.id ? 'Generating…' : 'Sequence →'}
                          </button>
                        </div>
                        {e.insight && <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#666', fontStyle: 'italic', lineHeight: '1.4' }}>{e.insight}</p>}
                        {e.buying_signals && <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#276749', lineHeight: '1.4' }}>{e.buying_signals.substring(0, 200)}...</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ borderTop: '0.5px solid #E8E4E0', paddingTop: '1.25rem' }}>
              <SectionLabel>Send signal summary to client</SectionLabel>
              {sendSuccess ? (
                <p style={{ color: '#276749', fontSize: '0.85rem', margin: 0 }}>Sent successfully.</p>
              ) : showSendForm ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <input placeholder="Client email" value={sendForm.to_email} onChange={e => setSendForm({ ...sendForm, to_email: e.target.value })} style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  <input placeholder="Client name" value={sendForm.to_name} onChange={e => setSendForm({ ...sendForm, to_name: e.target.value })} style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  <input placeholder="Subject line" value={sendForm.subject} onChange={e => setSendForm({ ...sendForm, subject: e.target.value })} style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleSend} disabled={sending} style={{ flex: 1, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>{sending ? 'Sending...' : 'Send'}</button>
                    <button onClick={() => setShowSendForm(false)} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer', color: '#888' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowSendForm(true)} style={{ width: '100%', background: 'none', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.5rem', fontSize: '0.85rem', cursor: 'pointer', color: '#8B1A1A' }}>Send to client</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();
  const { user } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [contactPicker, setContactPicker] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [platformActivity, setPlatformActivity] = useState([]);
  const [sweepLogs, setSweepLogs] = useState(null);
  const [readSignalIds, setReadSignalIds] = useState(() => {
    const userId = user?.id;
    if (!userId) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(`ce_read_signals_${userId}`) || '[]')); }
    catch { return new Set(); }
  });
  const activityTimerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/dashboard-v2`).then(r => r.json()).then(setData).catch(() => setError('Could not load dashboard.')).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    function loadActivity() {
      fetch(`${API_BASE}/admin/activity`)
        .then(r => r.json())
        .then(rows => setPlatformActivity(Array.isArray(rows) ? rows.slice(0, 20) : []))
        .catch(() => {});
    }
    loadActivity();
    activityTimerRef.current = setInterval(loadActivity, 30000);
    fetch(API_BASE + '/admin/sweep-logs').then(r => r.json()).then(setSweepLogs).catch(() => {});

    return () => clearInterval(activityTimerRef.current);
  }, [isSuperAdmin]);

  // Sync readSignalIds from localStorage once user is loaded
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`ce_read_signals_${userId}`) || '[]');
      setReadSignalIds(new Set(stored));
    } catch {}
  }, [user?.id]);

  function markRead(signalId) {
    const userId = user?.id;
    setReadSignalIds(prev => {
      if (prev.has(signalId)) return prev;
      const next = new Set(prev);
      next.add(signalId);
      if (userId) {
        localStorage.setItem(`ce_read_signals_${userId}`, JSON.stringify([...next]));
        window.dispatchEvent(new Event('ce:signalRead'));
      }
      return next;
    });
  }

  function handleGenerateSequence(contactId, clientId) {
    setSelectedSignal(null);
    setContactPicker(null);
    const toastId = toast.loading('Generating sequence — this can take up to 60 seconds...', { duration: 90000 });
    navigate(`/clients/${clientId}/sequences?contactId=${contactId}&generating=true`);
    creditAwareFetch(`${API_BASE}/contacts/${contactId}/generate-sequence`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: contactId, client_id: clientId })
    }).then(r => {
      if (!r) { toast.dismiss(toastId); return null; }
      if (r.status === 499) { toast('Sequence generation stopped', { id: toastId, icon: '⏹️' }); return null; }
      if (!r.ok) { toast.error('Sequence generation failed', { id: toastId }); return null; }
      return r.json();
    }).then(seq => {
      if (!seq) return;
      return fetch(`${API_BASE}/contacts/${contactId}/sequences`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, email_sequence: seq.email_sequence, linkedin_sequence: seq.linkedin_sequence, call_sequence: seq.call_sequence })
      }).then(() => toast.success('Sequence generated', { id: toastId }));
    }).catch(e => { console.error(e); toast.error('Sequence generation failed', { id: toastId }); });
  }

  async function handleSequenceFromSignal(signal) {
    try {
      const contacts = await fetch(`${API_BASE}/companies/${signal.company_id}/contacts`).then(r => r.json());
      if (!contacts?.length) return;
      if (contacts.length === 1) handleGenerateSequence(contacts[0].id, contacts[0].client_id);
      else setContactPicker({ signal, contacts });
    } catch (e) {}
  }

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const totalCompanies = data?.clients?.reduce((s, c) => s + parseInt(c.company_count || 0), 0) || 0;
  const totalContacts = data?.clients?.reduce((s, c) => s + parseInt(c.contact_count || 0), 0) || 0;

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Good morning.</h1>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{today} — here is what needs your attention today.</p>
        </div>

        {error && <p style={{ color: '#C53030', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1,2,3].map(i => <div key={i} style={{ height: '80px', background: '#EEECEA', borderRadius: '10px' }} />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '2rem', alignItems: 'start' }}>

            {/* LEFT */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', minWidth: 0 }}>

              {/* SIGNALS */}
              <div>
                <SectionLabel count={data?.today_signals?.length || 0}>Today's signals</SectionLabel>
                {!data?.today_signals?.length ? (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No new signals in the last 48 hours. The sweep runs daily at 06:00 UTC.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {data.today_signals.map(signal => {
                      const c = SIGNAL_COLOURS[signal.signal_type] || { bg: '#F7F7F7', text: '#888', border: '#888' };
                      const isRead = readSignalIds.has(signal.id);
                      return (
                        <div key={signal.id} style={{ background: isRead ? '#FAFAF8' : '#fff', border: '0.5px solid #E8E4E0', borderLeft: `3px solid ${isRead ? '#D4CFC9' : c.border}`, borderRadius: '10px', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', transition: 'border-color 0.2s, background 0.2s' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                              {!isRead && <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: c.border, flexShrink: 0, display: 'inline-block' }} />}
                              <Tag colour={isRead ? '#AAA' : c.text} bg={isRead ? '#F7F7F7' : c.bg}>{SIGNAL_LABELS[signal.signal_type] || signal.signal_type}</Tag>
                              <span style={{ fontSize: '0.7rem', fontWeight: '600', color: isRead ? '#AAA' : c.text }}>Score {signal.relevance_score}/10</span>
                              <span style={{ fontSize: '0.72rem', color: '#AAA' }}>{signal.client_name}</span>
                            </div>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: '600', color: isRead ? '#888' : '#8B1A1A' }}>{signal.company_name}</p>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.85rem', color: isRead ? '#888' : '#1A1A1A', lineHeight: '1.5' }}>{signal.signal_data?.summary}</p>
                            <p style={{ margin: 0, fontSize: '0.72rem', color: '#AAA' }}>{signal.contact_count} contact{signal.contact_count !== 1 ? 's' : ''} in workspace · {fmt(signal.created_at)}</p>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                            <button onClick={() => { markRead(signal.id); setSelectedSignal(signal); }} style={{ fontSize: '0.78rem', color: isRead ? '#AAA' : '#4A5568', background: 'none', border: `1px solid ${isRead ? '#E8E4E0' : '#D4CFC9'}`, borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>View detail</button>
                            <button onClick={() => handleSequenceFromSignal(signal)} style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>Sequence →</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* PRIORITY CONTACTS */}
              <div>
                <SectionLabel count={data?.priority_contacts?.length || 0}>Priority contacts — scored, no sequence yet</SectionLabel>
                {!data?.priority_contacts?.length ? (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No high-scoring unsequenced contacts. Run enrichment and scoring on your contacts to populate this.</p>
                  </div>
                ) : (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                    {data.priority_contacts.map((ct, i) => {
                      const score = ct.total_score;
                      const scoreColour = score >= 8 ? '#276749' : score >= 6 ? '#B7791F' : '#888';
                      const scoreBg = score >= 8 ? '#F0FFF4' : score >= 6 ? '#FFFBEB' : '#F7F7F7';
                      return (
                        <div key={ct.contact_id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.25rem', borderBottom: i < data.priority_contacts.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                              <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.full_name}</p>
                              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: scoreColour, backgroundColor: scoreBg, padding: '0.2rem 0.5rem', borderRadius: '20px', flexShrink: 0 }}>{score}/10</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.job_title} · <span style={{ color: '#8B1A1A' }}>{ct.company_name}</span> · {ct.client_name}</p>
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                            {ct.enrichment_data?.insight && (
                              <button onClick={() => setSelectedContact(ct)} style={{ fontSize: '0.78rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>View</button>
                            )}
                            <button onClick={() => handleGenerateSequence(ct.contact_id, ct.client_id)} disabled={generatingFor === ct.contact_id}
                              style={{ fontSize: '0.78rem', color: '#fff', background: generatingFor === ct.contact_id ? '#AAA' : '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>
                              {generatingFor === ct.contact_id ? 'Generating…' : 'Sequence →'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* PENDING */}
              {data?.pending_sequences?.length > 0 && (
                <div>
                  <SectionLabel count={data.pending_sequences.length}>Pending approval</SectionLabel>
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #B7791F', borderRadius: '10px', overflow: 'hidden' }}>
                    {data.pending_sequences.map((seq, i) => (
                      <div key={seq.id} onClick={() => navigate(`/clients/${seq.client_id}/sequences?contactId=${seq.contact_id}`)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.25rem', borderBottom: i < data.pending_sequences.length - 1 ? '0.5px solid #F0EDE9' : 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFAF8'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>{seq.contact_name}</p>
                          <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{seq.company_name} · {seq.client_name}</p>
                        </div>
                        <span style={{ fontSize: '0.7rem', color: '#B7791F', backgroundColor: '#FFFBEB', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500', flexShrink: 0 }}>approve →</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <SectionLabel>Overview</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                  {[
                    { label: 'Clients', value: data?.clients?.length || 0, accent: '#8B1A1A' },
                    { label: 'Pending', value: data?.pending_sequences?.length || 0, accent: '#B7791F' },
                    { label: 'Companies', value: totalCompanies, accent: '#276749' },
                    { label: 'Contacts', value: totalContacts, accent: '#1A56A0' },
                  ].map(s => (
                    <div key={s.label} style={{ backgroundColor: s.accent, borderRadius: '10px', padding: '1rem' }}>
                      <p style={{ margin: '0 0 0.25rem', fontSize: '1.6rem', fontWeight: '500', color: '#fff', lineHeight: 1 }}>{s.value}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <SectionLabel>Clients</SectionLabel>
                  <button onClick={() => navigate('/clients')} style={{ background: 'none', border: 'none', color: '#8B1A1A', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>View all</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {data?.clients?.map(client => (
                    <div key={client.id} onClick={() => navigate('/clients/' + client.id)}
                      style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '7px', background: '#F5E6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '500', color: '#8B1A1A', flexShrink: 0 }}>
                        {client.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: '500', fontSize: '0.85rem', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</p>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: '#888' }}>{client.company_count || 0} companies · {client.contact_count || 0} contacts</p>
                      </div>
                      <span style={{ fontSize: '0.68rem', color: '#276749', backgroundColor: '#F0FFF4', padding: '0.15rem 0.45rem', borderRadius: '20px', fontWeight: '500', flexShrink: 0 }}>{client.status}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>Recent activity</SectionLabel>
                <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                  {!data?.recent_activity?.length ? (
                    <p style={{ padding: '1.5rem', color: '#888', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>No activity yet.</p>
                  ) : data.recent_activity.map((a, i) => (
                    <div key={a.id} style={{ display: 'flex', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < data.recent_activity.length - 1 ? '0.5px solid #F0EDE9' : 'none', alignItems: 'flex-start' }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: ACTION_COLOURS[a.action_type] || '#888', flexShrink: 0, marginTop: '0.35rem' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ACTION_LABELS[a.action_type] || a.action_type}</p>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#AAA', whiteSpace: 'nowrap', marginLeft: '0.5rem', flexShrink: 0 }}>{fmt(a.created_at)}</p>
                        </div>
                        <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: '#888' }}>{a.client_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {isSuperAdmin && sweepLogs && (() => {
                const scheduled = sweepLogs.logs || [];
                const onDemand = sweepLogs.on_demand_logs || [];
                const scheduledCost = scheduled.reduce((s, l) => s + parseFloat(l.estimated_cost_usd || 0), 0);
                const onDemandCost = onDemand.reduce((s, l) => s + parseFloat(l.estimated_cost_usd || 0), 0);
                const scheduledSignals = scheduled.reduce((s, l) => s + parseInt(l.signals_found || 0), 0);
                const onDemandSignals = onDemand.reduce((s, l) => s + parseInt(l.signals_found || 0), 0);
                return (
                <>
                <div style={{ marginBottom: "1.5rem" }}>
                  <SectionLabel>Exa — scheduled sweep cost</SectionLabel>
                  <div style={{ background: "#fff", border: "0.5px solid #E8E4E0", borderRadius: "10px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: scheduled.length > 0 ? "0.5px solid #E8E4E0" : "none" }}>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#8B1A1A" }}>{"$" + scheduledCost.toFixed(4)}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>30-day cost</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{scheduledSignals}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Signals found</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{scheduled.length}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Sweeps run</p>
                      </div>
                    </div>
                    {scheduled.slice(0, 5).map((log, i) => (
                      <div key={log.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.55rem 1rem", borderBottom: i < Math.min(scheduled.length, 5) - 1 ? "0.5px solid #F0EDE9" : "none" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: "0.78rem", color: "#1A1A1A", fontWeight: "500" }}>{new Date(log.swept_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</p>
                          <p style={{ margin: "0.1rem 0 0", fontSize: "0.7rem", color: "#888" }}>{log.companies_swept} swept · {log.companies_skipped} skipped · {log.signals_found} signals</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p style={{ margin: 0, fontSize: "0.78rem", fontWeight: "600", color: "#276749" }}>{"$" + parseFloat(log.estimated_cost_usd || 0).toFixed(4)}</p>
                          <p style={{ margin: 0, fontSize: "0.68rem", color: "#888" }}>{log.exa_queries} queries</p>
                        </div>
                      </div>
                    ))}
                    {scheduled.length === 0 && (
                      <p style={{ padding: "1.5rem", color: "#888", fontSize: "0.85rem", margin: 0, textAlign: "center" }}>No scheduled sweeps yet. First runs at 06:00 UTC.</p>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: "2rem" }}>
                  <SectionLabel>Claude — on-demand scan cost</SectionLabel>
                  <div style={{ background: "#fff", border: "0.5px solid #E8E4E0", borderRadius: "10px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: onDemand.length > 0 ? "0.5px solid #E8E4E0" : "none" }}>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#8B1A1A" }}>{"$" + onDemandCost.toFixed(2)}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>30-day cost</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{onDemandSignals}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Signals found</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{onDemand.length}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Scans run</p>
                      </div>
                    </div>
                    {onDemand.slice(0, 5).map((log, i) => (
                      <div key={log.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.55rem 1rem", borderBottom: i < Math.min(onDemand.length, 5) - 1 ? "0.5px solid #F0EDE9" : "none" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: "0.78rem", color: "#1A1A1A", fontWeight: "500" }}>{new Date(log.swept_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                          <p style={{ margin: "0.1rem 0 0", fontSize: "0.7rem", color: "#888" }}>{log.signals_found} signals found</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p style={{ margin: 0, fontSize: "0.78rem", fontWeight: "600", color: "#B7791F" }}>~{"$" + parseFloat(log.estimated_cost_usd || 0).toFixed(2)}</p>
                          <p style={{ margin: 0, fontSize: "0.68rem", color: "#888" }}>estimated</p>
                        </div>
                      </div>
                    ))}
                    {onDemand.length === 0 && (
                      <p style={{ padding: "1.5rem", color: "#888", fontSize: "0.85rem", margin: 0, textAlign: "center" }}>No on-demand scans logged yet.</p>
                    )}
                  </div>
                </div>
                </>
                );
              })()}
              {isSuperAdmin && sweepLogs && false && (
                <div style={{ marginBottom: "2rem" }}>
                  <div style={{ background: "#fff", border: "0.5px solid #E8E4E0", borderRadius: "10px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "0.5px solid #E8E4E0" }}>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#8B1A1A" }}>{"$" + parseFloat(sweepLogs.totals?.total_cost_usd || 0).toFixed(2)}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>30-day cost</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", borderRight: "0.5px solid #E8E4E0", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{sweepLogs.totals?.total_signals || 0}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Signals found</p>
                      </div>
                      <div style={{ padding: "0.85rem 1rem", textAlign: "center" }}>
                        <p style={{ margin: "0 0 0.2rem", fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A" }}>{sweepLogs.totals?.sweep_count || 0}</p>
                        <p style={{ margin: 0, fontSize: "0.68rem", color: "#888", textTransform: "uppercase" }}>Sweeps run</p>
                      </div>
                    </div>
                    {sweepLogs.logs?.slice(0, 7).map((log, i) => (
                      <div key={log.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.55rem 1rem", borderBottom: i < Math.min(sweepLogs.logs.length, 7) - 1 ? "0.5px solid #F0EDE9" : "none" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: "0.78rem", color: "#1A1A1A", fontWeight: "500" }}>
                            {new Date(log.swept_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                            {log.error_message && <span style={{ color: "#C53030", marginLeft: "0.4rem", fontSize: "0.7rem" }}>Failed</span>}
                          </p>
                          <p style={{ margin: "0.1rem 0 0", fontSize: "0.7rem", color: "#888" }}>
                            {log.companies_swept} swept · {log.companies_skipped} skipped · {log.signals_found} signals
                          </p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p style={{ margin: 0, fontSize: "0.78rem", fontWeight: "600", color: parseFloat(log.estimated_cost_usd) > 1 ? "#B7791F" : "#276749" }}>
                            {"$" + parseFloat(log.estimated_cost_usd || 0).toFixed(4)}
                          </p>
                          <p style={{ margin: 0, fontSize: "0.68rem", color: "#888" }}>{log.exa_queries} queries</p>
                        </div>
                      </div>
                    ))}
                    {(!sweepLogs.logs || sweepLogs.logs.length === 0) && (
                      <p style={{ padding: "1.5rem", color: "#888", fontSize: "0.85rem", margin: 0, textAlign: "center" }}>No sweeps logged yet. First sweep runs at 06:00 UTC.</p>
                    )}
                  </div>
                </div>
              )}
              {isSuperAdmin && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <SectionLabel>Platform activity</SectionLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#38A169', boxShadow: '0 0 0 2px rgba(56,161,105,0.25)', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.68rem', color: '#AAA' }}>live · 30s</span>
                    </div>
                  </div>
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                    {!platformActivity.length ? (
                      <p style={{ padding: '1.5rem', color: '#888', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>No activity recorded yet.</p>
                    ) : platformActivity.map((a, i) => (
                      <div key={a.id} style={{ display: 'flex', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < platformActivity.length - 1 ? '0.5px solid #F0EDE9' : 'none', alignItems: 'flex-start' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: ACTION_COLOURS[a.action_type] || '#888', flexShrink: 0, marginTop: '0.38rem' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ACTION_LABELS[a.action_type] || a.action_type}</p>
                            <p style={{ margin: 0, fontSize: '0.68rem', color: '#AAA', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmt(a.created_at)}</p>
                          </div>
                          <p style={{ margin: '0.1rem 0 0', fontSize: '0.7rem', color: '#8B1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.client_name}{a.detail ? <span style={{ color: '#AAA' }}> · {a.detail}</span> : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedSignal && <SignalPanel signal={selectedSignal} onClose={() => setSelectedSignal(null)} onSequence={handleGenerateSequence} />}
      {selectedContact && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '480px', height: '100vh', backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1.5rem', borderBottom: '0.5px solid #E8E4E0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A' }}>{selectedContact.full_name}</h2>
                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: selectedContact.total_score >= 8 ? '#276749' : selectedContact.total_score >= 6 ? '#B7791F' : '#888', backgroundColor: selectedContact.total_score >= 8 ? '#F0FFF4' : selectedContact.total_score >= 6 ? '#FFFBEB' : '#F7F7F7', padding: '0.2rem 0.5rem', borderRadius: '20px' }}>{selectedContact.total_score}/10</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{selectedContact.job_title} · <span style={{ color: '#8B1A1A' }}>{selectedContact.company_name}</span></p>
              </div>
              <button onClick={() => setSelectedContact(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
              {selectedContact.enrichment_data?.insight ? (
                <div>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Insight</p>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#1A1A1A', lineHeight: '1.6', fontStyle: 'italic' }}>{selectedContact.enrichment_data.insight}</p>
                </div>
              ) : (
                <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No enrichment data available.</p>
              )}
            </div>
            <div style={{ padding: '1.25rem 1.5rem', borderTop: '0.5px solid #E8E4E0', flexShrink: 0 }}>
              <button onClick={() => { setSelectedContact(null); handleGenerateSequence(selectedContact.contact_id, selectedContact.client_id); }}
                style={{ width: '100%', backgroundColor: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' }}>
                Sequence →
              </button>
            </div>
          </div>
        </div>
      )}
      {contactPicker && <ContactPicker companyName={contactPicker.signal.company_name} contacts={contactPicker.contacts} generatingFor={generatingFor} onSelect={handleGenerateSequence} onClose={() => setContactPicker(null)} />}
    </div>
  );
}

export default Dashboard;
